import { preprocessXAllOf } from '../dsv-schema-utils';
import { generateValue } from './generate-value';
import { resolveConditionals } from './conditionals';
import { createAjv } from './ajv-factory';

function isObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

/**
 * Generate a constraint-valid sample payload for a DSV schema.
 *
 * @param {object} schema - the live (finalSchema) JSON Schema; may carry x-allOf
 * @returns {{ payload: object|null, valid: boolean, unmet: string[] }}
 *   payload: the generated sample (null when there is no usable schema).
 *   valid:   whether payload passes full AJV validation.
 *   unmet:   human-readable notes (conditional-trigger conflicts and/or AJV
 *            error messages); may be non-empty even when valid === true.
 */
export function generateSamplePayload(schema) {
  if (!isObject(schema) || !schema.type) {
    return { payload: null, valid: false, unmet: ['No schema to generate from'] };
  }

  // x-allOf → allOf on a deep clone; the input schema is never mutated.
  const processed = preprocessXAllOf(schema);

  let payload;
  const unmet = [];
  try {
    const { effectiveSchema, forced, unmetTriggers } = resolveConditionals(processed, generateValue);
    payload = { ...generateValue(effectiveSchema), ...forced };
    unmetTriggers.forEach((i) => {
      unmet.push(`Conditional rule #${i + 1} shown in baseline form (could not be triggered without conflict)`);
    });
  } catch (e) {
    return { payload: {}, valid: false, unmet: [`Generation error: ${e.message}`] };
  }

  // Self-verify with the same AJV semantics as the Validate button.
  let valid = false;
  try {
    const validate = createAjv().compile(processed);
    valid = validate(payload);
    if (!valid) {
      (validate.errors || []).forEach((err) => {
        unmet.push(`${err.instancePath || '/'} ${err.message}`);
      });
    }
  } catch (e) {
    unmet.push(`Schema compile error: ${e.message}`);
  }

  return { payload, valid, unmet };
}
